import { ShoppingBag } from 'lucide-react';
import { Toggle } from '../../../ui/Toggle';
import { ProductLinkInput } from '../../../ui/ProductLinkInput';
import { ProductManualInputs } from '../../../ui/ProductManualInputs';
import SearchPreviousProducts from '../../../../onboarding/components/SearchPreviousProducts';
import { dictionary } from '../../../../../utils/dictionary';
import { useAuth } from '../../../../../context/AuthContext';
import { useTutorialContext } from '../../../../../context/TutorialContext';
import { useOnboardingStore } from '../../../../../store/onboardingStore';
import { useUpsells } from '../hooks/useUpsells';
import { MethodSelector } from './MethodSelector';

interface UpsellCardProps {
  isScraping?: boolean;
}

export function UpsellCard({ isScraping = false }: UpsellCardProps) {
  const { language } = useAuth();
  const { setCurrentStepIndex, resumeTutorial } = useTutorialContext();
  const { previousProducts, isPreviousProductsLoading } = useOnboardingStore();
  const {
    upsells,
    toggleEnabled,
    methods,
    imageUploadStates,
    handleToggle,
    handleAdd,
    handleChange,
    setMethod,
    handlePreviousSelect,
    handleImageSelect,
  } = useUpsells();

  const currentUpsell = upsells[0];
  const currentMethod = currentUpsell ? methods.get(currentUpsell.id) ?? null : null;

  const handleUpsell = () => {
    setCurrentStepIndex(3);
    resumeTutorial();
  };

  return (
    <div
      id="upsell-target"
      className={`${toggleEnabled ? 'bg-[#FBFAFF] border-[#EFECFF] solid border-2' : 'bg-[#EFECFF]'} from-purple-50 to-pink-50 rounded-[25px] px-6 py-4`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center bg-white">
            <ShoppingBag className="w-7 h-7 text-[#6D29D9]" />
          </div>
          <div>
            <h3 className="text-md font-bold text-gray-900">{dictionary['add-upsell'][language]}</h3>
            <p
              onClick={handleUpsell}
              className="text-xs text-[#62748E] hover:text-blue-500 hover:underline transition-colors cursor-pointer"
            >
              {dictionary['what-is-an-upsell'][language]}
            </p>
          </div>
        </div>
        <Toggle enabled={toggleEnabled} onChange={handleToggle} />
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          toggleEnabled ? 'opacity-100 max-h-[500px]' : 'opacity-0 max-h-0'
        }`}
      >
        {!currentUpsell && toggleEnabled && (
          <button type="button" onClick={handleAdd} className="mt-3 text-sm text-[#6D29D9] hover:underline">
            + {dictionary['add-upsell'][language]}
          </button>
        )}

        {currentUpsell && (
          <>
            <MethodSelector
              currentMethod={currentMethod}
              onSelect={(m) => setMethod(currentUpsell.id, m)}
              disabled={isScraping}
            />

            {currentMethod === 'link' && (
              <div className="relative mt-3">
                <ProductLinkInput
                  value={currentUpsell.productLink || ''}
                  placeholder={dictionary['paste-product-url-placeholder'][language]}
                  onChange={(value) => handleChange(currentUpsell.id, 'productLink', value)}
                />
              </div>
            )}

            {currentMethod === 'manual' && (
              <div className="mt-3 space-y-3">
                <ProductManualInputs
                  manualData={{
                    images: Array.isArray(currentUpsell.url_images)
                      ? currentUpsell.url_images
                      : currentUpsell.url_images
                        ? [currentUpsell.url_images as string]
                        : [],
                    name: currentUpsell.productName || '',
                    price: currentUpsell.price || '',
                    description: currentUpsell.description || '',
                  }}
                  handleImageUpload={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageSelect(file, currentUpsell.id);
                  }}
                  handleRemoveImage={() => handleChange(currentUpsell.id, 'url_images', undefined)}
                  handleManualDataChange={(field, value) => {
                    const map: Record<string, string> = { name: 'productName', price: 'price', description: 'description' };
                    handleChange(currentUpsell.id, map[field] as keyof typeof currentUpsell, value);
                  }}
                  isUploadingImages={imageUploadStates.get(currentUpsell.id)?.isLoading ?? false}
                />
              </div>
            )}

            {currentMethod === 'previous' && (
              <div className="mt-3 space-y-3">
                <SearchPreviousProducts
                  previousProducts={previousProducts}
                  handlePreviousProductSelect={(e) => {
                    const product = previousProducts.find((p) => p.id === e.target.value);
                    if (product) handlePreviousSelect(product, currentUpsell.id);
                  }}
                  isPreviousProductsLoading={isPreviousProductsLoading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
